import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { FacultyForm, AvailabilityForm } from '../components/faculty';
import { useFaculty } from '../hooks/useFaculty';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Edit2, X, Mail, Phone, Calendar, Briefcase, BookOpen, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordResetCard } from '../components/PasswordResetCard';
import { facultyApi } from '../api/facultyApi';
import { parseApiError } from '../lib/apiErrors';

export default function FacultyProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [faculty, setFaculty] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { isAdmin } = useAuth();
  const {
    fetchFacultyById, 
    updateFaculty, 
    deactivateFaculty,
    addAvailability, 
    deleteAvailability, 
    loading 
  } = useFaculty();

  const loadFaculty = useCallback(async () => {
    try {
      const data = await fetchFacultyById(id);
      setFaculty(data);
    } catch (err) {
      toast.error('Failed to load faculty profile');
      navigate('/faculty');
    }
  }, [fetchFacultyById, id, navigate]);

  useEffect(() => {
    loadFaculty();
  }, [loadFaculty]);

  const handleUpdate = async (data) => {
    try {
      await updateFaculty(id, data);
      toast.success('Faculty updated successfully!');
      setIsEditing(false);
      loadFaculty();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to update faculty').message);
    }
  };

  const handleDeactivate = async () => {
    if (window.confirm('Are you sure you want to deactivate this faculty member?')) {
      try {
        await deactivateFaculty(id);
        toast.success('Faculty deactivated');
        loadFaculty();
      } catch (err) {
        toast.error('Failed to deactivate faculty');
      }
    }
  };

  const handleAddAvailability = async (data) => {
    try {
      await addAvailability(id, data);
      toast.success('Availability added');
      loadFaculty();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to add availability').message);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    try {
      await deleteAvailability(availabilityId);
      toast.success('Availability removed');
      loadFaculty();
    } catch (err) {
      toast.error('Failed to remove availability');
    }
  };

  if (!faculty) {
    return (
      <DashboardLayout title="Faculty Profile">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48"></div>
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Faculty Profile">
      <div className="animate-fade-in space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(location.pathname.startsWith('/admin') ? '/admin/faculty' : '/faculty/members')}
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
            Back to Faculty List
          </button>
          
          <div className="flex items-center gap-2">
            {faculty.status === 'active' && !isEditing && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 border border-primary text-primary hover:bg-primary/10 dark:border-accent-cool dark:text-accent-cool dark:hover:bg-accent-cool/10 bg-transparent rounded-md px-4 py-2 transition-colors font-medium"
                  data-testid="edit-faculty-btn"
                >
                  <Edit2 size={18} />
                  Edit
                </button>
                <button
                  onClick={handleDeactivate}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 transition-colors font-medium"
                  data-testid="deactivate-btn"
                >
                  <UserX size={18} />
                  Deactivate
                </button>
              </>
            )}
            {isEditing && (
              <button
                onClick={() => setIsEditing(false)}
                className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                data-testid="cancel-edit-btn"
              >
                <X size={18} />
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary-light p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">
                {faculty.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white" data-testid="faculty-name">
                  {faculty.name}
                </h2>
                <span 
                  className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    faculty.status === 'active'
                      ? 'bg-emerald-400/20 text-emerald-100'
                      : 'bg-slate-400/20 text-slate-200'
                  }`}
                  data-testid="faculty-status-badge"
                >
                  {faculty.status}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {isEditing ? (
              <FacultyForm 
                initialData={faculty}
                onSubmit={handleUpdate}
                loading={loading}
                isEdit
              />
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                      <Mail className="text-primary dark:text-accent-cool" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Email</p>
                      <p className="text-slate-900 dark:text-white font-medium" data-testid="faculty-email">
                        {faculty.email}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                      <Phone className="text-primary dark:text-accent-cool" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Phone</p>
                      <p className="text-slate-900 dark:text-white font-medium" data-testid="faculty-phone">
                        {faculty.phone}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                      <Briefcase className="text-primary dark:text-accent-cool" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Employment Type</p>
                      <p className="text-slate-900 dark:text-white font-medium capitalize" data-testid="faculty-employment">
                        {faculty.employment_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                      <Calendar className="text-primary dark:text-accent-cool" size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Joining Date</p>
                      <p className="text-slate-900 dark:text-white font-medium" data-testid="faculty-joining-date">
                        {new Date(faculty.joining_date).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="p-2 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                    <BookOpen className="text-primary dark:text-accent-cool" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Subjects</p>
                    <div className="flex flex-wrap gap-2" data-testid="faculty-subjects">
                      {faculty.subjects.length > 0 ? (
                        faculty.subjects.map((subject, idx) => (
                          <span
                            key={idx}
                            className="bg-primary/10 text-primary dark:bg-accent-cool/20 dark:text-accent-cool px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {subject}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">No subjects assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!isEditing && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
            <AvailabilityForm
              availability={faculty.availability || []}
              onAdd={handleAddAvailability}
              onDelete={handleDeleteAvailability}
              loading={loading}
            />
          </div>
        )}

        {!isEditing && isAdmin() && (
          <PasswordResetCard
            onReset={(newPassword) => facultyApi.resetPassword(id, { new_password: newPassword })}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
